import React, { useState, useEffect } from 'react';
import './styles.css';

const RealEstatePayment = () => {
  // ============================================================
  // STATE MANAGEMENT
  // ============================================================

  const [properties, setProperties] = useState([
    {
      id: 1,
      name: 'Luxury Apartment - Downtown',
      description: 'Premium 3BHK apartment in the heart of the city with modern amenities',
      price: 11.00,
      beds: 3,
      baths: 2,
      area: 1500,
      emoji: '🏢'
    },
    {
      id: 2,
      name: 'Villa - Riverside',
      description: 'Spacious 4BHK villa with private garden and river view',
      price: 15.00,
      beds: 4,
      baths: 3,
      area: 2000,
      emoji: '🏡'
    },
    {
      id: 3,
      name: 'Penthouse - City Center',
      description: 'Ultra-luxury penthouse with panoramic city views and top amenities',
      price: 20.00,
      beds: 5,
      baths: 4,
      area: 3000,
      emoji: '🏰'
    }
  ]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    mid: 'MID4CSVNTDS660',
    property_id: ''
  });

  // ============================================================
  // HANDLERS
  // ============================================================

  const openBookingModal = (property) => {
    setSelectedProperty(property);
    setFormData({
      ...formData,
      property_id: property.id
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProperty(null);
    setFormData({
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      mid: 'MID4CSVNTDS660',
      property_id: ''
    });
    setFormErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Phone number formatter
    if (name === 'customer_phone') {
      const formatted = value.replace(/[^0-9]/g, '').substring(0, 10);
      setFormData({ ...formData, [name]: formatted });
      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  const validateForm = () => {
    const errors = {};
    const { customer_name, customer_email, customer_phone } = formData;

    // Validate name
    if (!customer_name || customer_name.length < 2) {
      errors.customer_name = 'Please enter your full name';
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customer_email)) {
      errors.customer_email = 'Please enter a valid email';
    }

    // Validate phone
    if (customer_phone.length !== 10 || !/^[6-9]/.test(customer_phone)) {
      errors.customer_phone = 'Please enter a valid 10-digit number starting with 6-9';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const submitBooking = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    // ============================================================
    // PREPARE PAYLOAD
    // ============================================================

    const payload = {
      mid: formData.mid,
      amount: selectedProperty.price,
      customer_name: formData.customer_name,
      customer_email: formData.customer_email,
      customer_phone: formData.customer_phone,
      description: `Property Booking - ${selectedProperty.name}`,
      order_id: `ORDER-${Date.now()}`
    };

    console.log('📤 Sending Payload:', payload);

    try {
      // ============================================================
      // CALL API: POST /api/payment/initiate
      // ============================================================

      const response = await fetch('https://api.a1xpay.com/api/payment/initiate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      console.log('✅ API Response:', data);

      if (data.success && data.data.checkout_url) {
        console.log('🔗 Checkout URL:', data.data.checkout_url);

        // Close modal
        closeModal();

        // Open checkout page in new tab
        setTimeout(() => {
          window.open(data.data.checkout_url, '_blank');
        }, 500);
      } else {
        setFormErrors({
          submit: data.message || 'Payment initiation failed'
        });
      }
    } catch (error) {
      console.error('❌ Error:', error);
      setFormErrors({
        submit: 'Network error. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================
  // COMPONENTS
  // ============================================================

  return (
    <div className="real-estate-page">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="logo">
            <i className="fas fa-building"></i>
            <span>Blake Collier</span>
          </div>
          <nav>
            <a href="#properties">Properties</a>
            <a href="#about">About</a>
            <a href="#contact">Contact</a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-badge">
          <i className="fas fa-star"></i> Premium Real Estate
        </div>
        <h1>Invest in Your Dream Property</h1>
        <p>Secure your future with Blake Collier's premium real estate portfolio</p>
      </section>

      {/* Properties Section */}
      <section id="properties" className="container">
        <div className="properties-section">
          <h2>Available Properties</h2>
          <div className="properties-grid">
            {properties.map(property => (
              <div key={property.id} className="property-card">
                <div className="property-image">{property.emoji}</div>
                <div className="property-content">
                  <div className="property-name">{property.name}</div>
                  <div className="property-desc">{property.description}</div>

                  <div className="property-features">
                    <div className="feature">
                      <i className="fas fa-bed"></i>
                      <span>{property.beds} Beds</span>
                    </div>
                    <div className="feature">
                      <i className="fas fa-bath"></i>
                      <span>{property.baths} Baths</span>
                    </div>
                    <div className="feature">
                      <i className="fas fa-expand"></i>
                      <span>{property.area} sqft</span>
                    </div>
                  </div>

                  <div className="property-price">₹{property.price.toFixed(2)}</div>

                  <button
                    className="booking-btn"
                    onClick={() => openBookingModal(property)}
                  >
                    <i className="fas fa-lock"></i>
                    Book Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Payment Modal */}
      {isModalOpen && selectedProperty && (
        <div className="modal show" onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <div className="modal-content">
            <div className="modal-header">
              <span>Book Property</span>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            <div id="modalBody">
              <form onSubmit={submitBooking}>
                {/* Property Info */}
                <div className="property-info-box">
                  <div className="info-label">Property</div>
                  <div className="info-name">{selectedProperty.name}</div>
                  <div className="info-price">₹{selectedProperty.price.toFixed(2)}</div>
                </div>

                {/* Customer Name */}
                <div className="form-group">
                  <label htmlFor="customer_name">Full Name *</label>
                  <input
                    type="text"
                    id="customer_name"
                    name="customer_name"
                    placeholder="Enter your full name"
                    value={formData.customer_name}
                    onChange={handleInputChange}
                    className={formErrors.customer_name ? 'error' : ''}
                    required
                  />
                  {formErrors.customer_name && (
                    <div className="error-text show">{formErrors.customer_name}</div>
                  )}
                </div>

                {/* Email */}
                <div className="form-group">
                  <label htmlFor="customer_email">Email Address *</label>
                  <input
                    type="email"
                    id="customer_email"
                    name="customer_email"
                    placeholder="your@email.com"
                    value={formData.customer_email}
                    onChange={handleInputChange}
                    className={formErrors.customer_email ? 'error' : ''}
                    required
                  />
                  {formErrors.customer_email && (
                    <div className="error-text show">{formErrors.customer_email}</div>
                  )}
                </div>

                {/* Phone */}
                <div className="form-group">
                  <label htmlFor="customer_phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="customer_phone"
                    name="customer_phone"
                    placeholder="9876543210"
                    maxLength="10"
                    pattern="[6-9][0-9]{9}"
                    value={formData.customer_phone}
                    onChange={handleInputChange}
                    className={formErrors.customer_phone ? 'error' : ''}
                    required
                  />
                  <div className="helper-text">10-digit mobile number</div>
                  {formErrors.customer_phone && (
                    <div className="error-text show">{formErrors.customer_phone}</div>
                  )}
                </div>

                {/* Submit Errors */}
                {formErrors.submit && (
                  <div className="error-text show" style={{ display: 'block', marginTop: '10px' }}>
                    {formErrors.submit}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  className={`submit-btn ${isLoading ? 'loading' : ''}`}
                  disabled={isLoading}
                >
                  <span className="btn-text">Proceed to Payment</span>
                  {isLoading && <div className="spinner"></div>}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="footer">
        <p><strong>Blake Collier - Real Estate</strong></p>
        <p>
          <i className="fas fa-phone"></i> +91-9876543210 |{' '}
          <i className="fas fa-envelope"></i> contact@blakecollier.com
        </p>
        <p style={{ marginTop: '20px', fontSize: '12px', opacity: 0.7 }}>
          © 2026 Blake Collier. All rights reserved. Powered by A1XPAY
        </p>
      </footer>
    </div>
  );
};

export default RealEstatePayment;